import React, { useEffect, useRef, useState } from "react";
import { getCameraService } from "../../services/cameraService";
import debugx from "debugx";

const debug = debugx("bikedash:camera-overlay");

interface CameraOverlayProps {
  onLongPress?: () => void;
  onSwipeDown?: () => void;
}

export const CameraOverlay: React.FC<CameraOverlayProps> = ({
  onLongPress,
  onSwipeDown,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [cameraType, setCameraType] = useState<"bluetooth" | "phone">("phone");
  const [layout, setLayout] = useState<
    "regular" | "ultrawide" | "full" | "full-wide"
  >("regular");
  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth > window.innerHeight,
  );
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const longPressTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const cameraService = getCameraService();

    // Set canvas for bluetooth camera
    if (canvasRef.current) {
      cameraService.setCanvas(canvasRef.current);
      debug("Canvas set for camera overlay (bluetooth)");
    }

    // Set video element for phone camera
    if (videoRef.current) {
      cameraService.setVideoElement(videoRef.current);
      debug("Video element set for camera overlay (phone)");
    }

    // Check camera state periodically (poll every 500ms)
    // Show overlay only when camera is active (not in OFF mode)
    const checkCameraState = () => {
      const active = cameraService.isActive();
      const settings = cameraService.getSettings();
      setIsVisible(active);
      setLayout(settings.layout);
      setCameraType(settings.type);
    };

    checkCameraState(); // Initial check
    const interval = setInterval(checkCameraState, 500);

    // Listen for orientation changes
    const handleResize = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
      debug("Orientation changed: %s", landscape ? "landscape" : "portrait");
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };

    // Start long press timer
    longPressTimerRef.current = window.setTimeout(() => {
      debug("Long press detected");
      onLongPress?.();
      touchStartRef.current = null;
    }, 500);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStartRef.current.y;

    // Cancel long press if moved too much
    if (Math.abs(deltaY) > 10 && longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;

    // Swipe down detection (at least 50px down, within 300ms)
    if (deltaY > 50 && deltaTime < 300) {
      debug("Swipe down detected");
      onSwipeDown?.();
    }

    touchStartRef.current = null;
  };

  // Calculate layout-specific styles
  const getLayoutStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      touchAction: "none",
      pointerEvents: isVisible ? "auto" : "none",
      display: isVisible ? "block" : "none",
      border: "none", // No border
      backgroundColor: "transparent", // Transparent background
      zIndex: layout === "full" || layout === "full-wide" ? 1 : 2, // Fullscreen modes go below header, others above map
    };

    switch (layout) {
      case "full":
        // Fullscreen camera with cover - fills screen, may crop
        return {
          ...baseStyles,
          top: "0",
          left: "0",
          width: "100vw",
          height: "100vh",
          objectFit: "cover",
          borderRadius: "0",
        };

      case "full-wide":
        // Fullscreen camera with contain - letterboxed
        // In landscape: anchor to bottom (map at top)
        // In portrait: center (map overlays in corner)
        return {
          ...baseStyles,
          bottom: isLandscape ? "0" : "auto",
          top: isLandscape ? "auto" : "0",
          left: "0",
          width: "100vw",
          height: "100vh",
          objectFit: "contain",
          objectPosition: isLandscape ? "bottom" : "center", // Anchor to bottom in landscape, center in portrait
          borderRadius: "0",
        };

      case "ultrawide":
        // Ultra-wide cropped camera in top right
        // Taller in portrait mode for better visibility
        return {
          ...baseStyles,
          top: "0px",
          right: "0px",
          width: "480px", // Wider than regular
          height: isLandscape ? "180px" : "240px", // Taller in portrait mode
          objectFit: "cover",
          borderBottomLeftRadius: "20px",
          // Use CSS mask for gradient fade at bottom
          WebkitMaskImage:
            "linear-gradient(to bottom, black 60%, transparent 100%)",
          maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)",
        };

      case "regular":
      default:
        // Regular aspect ratio in top right
        return {
          ...baseStyles,
          top: "0px",
          right: "0px",
          width: "320px",
          height: "240px", // 4:3 aspect ratio
          objectFit: "cover",
          borderBottomLeftRadius: "20px",
          // Use CSS mask for gradient fade at bottom and left edges
          WebkitMaskImage:
            "linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to right, transparent 0%, black 15%)",
          maskImage:
            "linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to right, transparent 0%, black 15%)",
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
        };
    }
  };

  return (
    <>
      {/* Canvas for bluetooth camera */}
      <canvas
        ref={canvasRef}
        className="camera-overlay"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          ...getLayoutStyles(),
          display: isVisible && cameraType === "bluetooth" ? "block" : "none",
        }}
      />
      {/* Video for phone camera */}
      <video
        ref={videoRef}
        className="camera-overlay"
        autoPlay
        playsInline
        muted
        webkit-playsinline="true"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          ...getLayoutStyles(),
          display: isVisible && cameraType === "phone" ? "block" : "none",
        }}
      />
    </>
  );
};
